// ─────────────────────────────────────────────────────────────────────────────
// Dashboard inline SVGs, copied VERBATIM from CD's dashboard.html mockup.
// The brand mark + wordmark, platform glyphs (Spotify/Instagram/TikTok/YouTube/
// Facebook/Amazon/Apple), and the small stroke icons (arrow, bell, settings,
// message, plus, chevron). These are presentational; the components below feed
// them from the data layer but the paths themselves are CD's exact spec.
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX } from 'react';

export function BrandMark(): JSX.Element {
  return (
    <svg viewBox="0 0 317.48 317.48" aria-hidden="true">
      <polygon points="269 144.98 317.48 96.5 317.48 0 207.22 0 158.74 48.48 110.26 0 0 0 0 110.26 48.48 158.74 48.48 158.74 48.48 158.74 0 207.22 0 317.48 110.26 317.48 158.74 269 207.22 317.48 317.48 317.48 317.48 220.99 269 172.5 269 172.19 195.65 172.19 195.65 195.34 158.9 195.34 122.14 195.34 122.14 158.58 122.14 121.83 158.9 121.83 195.65 121.83 195.65 144.98 269 144.98" />
    </svg>
  );
}

/* acp 2026-07-24 — Taylor's PRO chip (artistcntrl_pro-81.svg, crimson variant),
   rendered beside the wordmark ONLY for ACP accounts (isPro gate in TopNav).
   Fills inlined (Taylor's export had #93152d; Ryan ruled ONE brand red, so the rect rides the accent token \u2192 #8B1A2B) so the
   mockup's .cls-* style classes can't collide with anything else on the page. */
export function BrandProBadge(): JSX.Element {
  return (
    <svg viewBox="0 0 68.79 36.03" aria-label="Pro">
      <rect style={{ fill: 'var(--ac-color-accent, #8B1A2B)' }} width="68.79" height="36.03" />
      <g>
        <path fill="#f4f4f4" d="M8.11,7.96h10.32c1.16,0,2.14.12,2.94.38.79.25,1.44.65,1.94,1.21s.86,1.28,1.09,2.17c.22.89.33,1.98.33,3.27s-.11,2.35-.33,3.24c-.22.89-.59,1.62-1.09,2.18-.5.56-1.15.97-1.94,1.22-.8.25-1.77.38-2.94.38h-5.9v6.06h-4.42V7.96ZM18.29,18.12c.57,0,1.03-.25,1.37-.75.34-.5.51-1.3.51-2.39s-.17-1.93-.51-2.41c-.34-.48-.8-.72-1.37-.72h-5.76v6.27h5.76Z" />
        <path fill="#f4f4f4" d="M26.31,7.96h10.32c1.16,0,2.14.11,2.94.32.79.21,1.44.56,1.94,1.05s.86,1.11,1.09,1.88c.22.77.33,1.71.33,2.82,0,1.04-.1,1.93-.29,2.67-.2.74-.51,1.35-.94,1.84-.43.48-.99.85-1.69,1.1-.7.25-1.55.4-2.55.46l6.46,7.99h-5.9l-5.76-7.96h-1.53v7.96h-4.42V7.96ZM36.49,16.22c.57,0,1.03-.17,1.37-.52.34-.35.51-.91.51-1.67s-.17-1.32-.51-1.66c-.34-.34-.8-.51-1.37-.51h-5.76v4.37h5.76Z" />
        <path fill="#f4f4f4" d="M44.72,18.02c0-1.91.17-3.53.5-4.85.33-1.32.84-2.4,1.54-3.23.7-.83,1.59-1.43,2.67-1.81,1.08-.38,2.37-.56,3.87-.56s2.79.19,3.87.56c1.08.38,1.97.98,2.67,1.81.7.83,1.21,1.91,1.54,3.23.33,1.32.5,2.94.5,4.85s-.17,3.5-.5,4.82c-.33,1.32-.84,2.4-1.54,3.23-.7.83-1.59,1.44-2.67,1.82-1.08.38-2.37.58-3.87.58s-2.79-.19-3.87-.58c-1.08-.38-1.97-.99-2.67-1.82-.7-.83-1.21-1.91-1.54-3.23-.33-1.32-.5-2.93-.5-4.82ZM49.28,18.02c0,1.18.08,2.18.24,3,.16.82.4,1.49.72,2,.32.51.74.88,1.25,1.1.51.22,1.11.33,1.81.33s1.33-.11,1.84-.33c.51-.22.92-.59,1.25-1.1.32-.51.56-1.17.71-2,.15-.82.23-1.82.23-3s-.08-2.2-.23-3.02c-.15-.81-.39-1.47-.71-1.98-.32-.51-.74-.88-1.25-1.1s-1.12-.33-1.84-.33-1.3.11-1.81.33-.92.59-1.25,1.1c-.32.51-.56,1.17-.72,1.98-.16.81-.24,1.82-.24,3.02Z" />
      </g>
    </svg>
  );
}

export function BrandWordmark(): JSX.Element {
  return (
    <svg viewBox="0 0 1046.86 113.71" aria-label="Artist CNTRL">
      <g>
        <path d="M28.28,2.19h21.87l-15.74,68.08h31.49L50.15,2.19h21.87l28.28,109.33h-24.93l-4.67-20.12H29.59l-4.67,20.12H0L28.28,2.19Z" />
        <path d="M104.67,2.19h56.12c6.32,0,11.64.58,15.96,1.75,4.32,1.17,7.85,3.06,10.57,5.68,2.72,2.62,4.69,6.03,5.9,10.21,1.21,4.18,1.82,9.28,1.82,15.31,0,5.64-.54,10.47-1.6,14.5-1.07,4.03-2.77,7.36-5.1,9.99s-5.39,4.62-9.18,5.98c-3.79,1.36-8.41,2.19-13.85,2.48l35.13,43.44h-32.07l-31.34-43.29h-8.31v43.29h-24.05V2.19ZM160.06,47.09c3.11,0,5.59-.95,7.43-2.84,1.84-1.9,2.77-4.93,2.77-9.11s-.92-7.19-2.77-9.04c-1.85-1.84-4.33-2.77-7.43-2.77h-31.34v23.76h31.34Z" />
        <path d="M236.15,23.32h-32.8V2.19h89.65v21.14h-32.8v88.19h-24.05V23.32Z" />
        <path d="M303.21,2.19h24.05v109.33h-24.05V2.19Z" />
        <path d="M385.43,0c4.57,0,8.72.27,12.46.8,3.74.54,7.26,1.31,10.57,2.33,3.3,1.02,6.46,2.28,9.48,3.79,3.01,1.51,6.02,3.23,9.04,5.17l-14.87,17.93c-3.4-2.53-7.22-4.52-11.44-5.98-4.23-1.46-9.26-2.19-15.09-2.19-7.78,0-13.17.58-16.18,1.75-3.01,1.17-4.52,3.01-4.52,5.54,0,1.65.7,3.23,2.11,4.74,1.41,1.51,3.28,2.92,5.61,4.23,2.33,1.31,5,2.55,8.02,3.72,3.01,1.17,6.12,2.24,9.33,3.21,6.8,2.04,12.63,4.21,17.49,6.49,4.86,2.28,8.82,4.88,11.88,7.8,3.06,2.92,5.32,6.2,6.78,9.84s2.19,7.9,2.19,12.75c0,11.18-3.99,19.27-11.95,24.27-7.97,5.01-19.39,7.51-34.26,7.51-5.44,0-10.18-.27-14.21-.8-4.03-.54-7.68-1.31-10.93-2.33-3.26-1.02-6.29-2.31-9.11-3.86-2.82-1.55-5.78-3.3-8.89-5.25l14.87-17.93c3.4,2.53,7.29,4.54,11.66,6.05,4.37,1.51,9.77,2.26,16.18,2.26,7.87,0,13.73-.85,17.57-2.55,3.84-1.7,5.76-4.01,5.76-6.92,0-1.46-.39-2.74-1.17-3.86-.78-1.12-2.09-2.19-3.94-3.21-1.85-1.02-4.28-2.09-7.29-3.21-3.01-1.12-6.76-2.45-11.22-4.01-6.22-2.23-11.79-4.52-16.69-6.85-4.91-2.33-9.09-4.96-12.54-7.87-3.45-2.92-6.08-6.27-7.87-10.06-1.8-3.79-2.7-8.26-2.7-13.41,0-10.59,3.74-18.22,11.22-22.89,7.48-4.66,18.37-7,32.65-7Z" />
        <path d="M467.35,23.32h-32.8V2.19h89.65v21.14h-32.8v88.19h-24.05V23.32Z" />
      </g>
      <g>
        <path d="M648.75,101.46c-3.01,2.04-6.05,3.82-9.11,5.32-3.06,1.51-6.29,2.79-9.69,3.86-3.4,1.07-7.07,1.85-11.01,2.33-3.94.49-8.29.73-13.05.73-8.26,0-15.45-1-21.57-2.99-6.12-1.99-11.15-5.22-15.09-9.69-3.94-4.47-6.88-10.35-8.82-17.64-1.95-7.29-2.92-16.18-2.92-26.68s.97-19.51,2.92-26.75c1.94-7.24,4.88-13.07,8.82-17.49,3.94-4.42,8.96-7.6,15.09-9.55,6.12-1.94,13.31-2.92,21.57-2.92,4.57,0,8.75.22,12.54.66s7.36,1.14,10.71,2.11c3.35.97,6.58,2.19,9.69,3.64,3.11,1.46,6.22,3.21,9.33,5.25l-14.72,17.79c-3.11-2.53-6.68-4.42-10.71-5.69-4.03-1.26-8.72-1.9-14.07-1.9-4.47,0-8.33.54-11.59,1.6-3.26,1.07-5.93,2.94-8.02,5.61-2.09,2.67-3.62,6.25-4.59,10.71-.97,4.47-1.46,10.11-1.46,16.91s.53,12.9,1.6,17.42c1.07,4.52,2.65,8.09,4.74,10.71,2.09,2.62,4.74,4.42,7.94,5.39,3.21.97,6.9,1.46,11.08,1.46,10.69,0,19.24-2.67,25.66-8.02l14.72,17.78Z" />
        <path d="M655.89,2.19h22.59l1.46,109.33h-24.05V2.19ZM678.48,2.19h20.85l30.91,109.33h-20.85l-30.9-109.33ZM752.83,111.52h-22.59l-1.46-109.33h24.05v109.33Z" />
        <path d="M795.83,23.32h-32.8V2.19h89.65v21.14h-32.8v88.19h-24.05V23.32Z" />
        <path d="M862.89,2.19h56.12c6.32,0,11.64.58,15.96,1.75,4.32,1.17,7.85,3.06,10.57,5.68,2.72,2.62,4.69,6.03,5.9,10.21,1.21,4.18,1.82,9.28,1.82,15.31,0,5.64-.54,10.47-1.6,14.5-1.07,4.03-2.77,7.36-5.1,9.99s-5.39,4.62-9.18,5.98c-3.79,1.36-8.41,2.19-13.85,2.48l35.13,43.44h-32.07l-31.34-43.29h-8.31v43.29h-24.05V2.19ZM918.28,47.09c3.11,0,5.59-.95,7.43-2.84,1.84-1.9,2.77-4.93,2.77-9.11s-.92-7.19-2.77-9.04c-1.85-1.84-4.33-2.77-7.43-2.77h-31.34v23.76h31.34Z" />
        <path d="M965.95,2.19h24.05v88.19h56.85v21.14h-80.91V2.19Z" />
      </g>
    </svg>
  );
}

// ── Official brand icons (served verbatim at /icons/) ────────────────────────
// These are the REAL brand logomarks (full color, with their own square/rounded
// backgrounds), not CD's hand-drawn currentColor glyphs. Rendered as <img> so
// they keep their native artwork; the host (.pc-icon / .mover__thumb) drops its
// colored background so the icons aren't double-styled. Filenames with spaces
// are URL-encoded. Source files: site/icons/*.svg (copied to dist/icons/*).

/** A brand logomark <img> from /icons/. `file` may contain spaces (encoded). */
export function BrandIcon({
  file,
  className,
}: {
  readonly file: string;
  readonly className?: string;
}): JSX.Element {
  // Encode each path segment so spaces (e.g. "Amazon Music") resolve.
  const src = '/icons/' + encodeURIComponent(file);
  return <img className={className} src={src} alt="" aria-hidden="true" />;
}

export const BRAND_ICON = {
  spotify: 'spotify-square-color-icon.svg',
  amazon: 'brandbird-Amazon Music-logomark.svg',
  apple: 'brandbird-Apple Music-logomark.svg',
  instagram: 'ig-instagram-icon.svg',
  facebook: 'facebook-square-icon.svg',
  tiktok: 'tiktok-rounded-square-icon.svg',
  youtube: 'youtube-color-icon.svg',
} as const;

// ── Platform glyphs (fill: currentColor) ─────────────────────────────────────

export function SpotifyGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.5 17.3a.75.75 0 0 1-1.03.25c-2.82-1.72-6.36-2.11-10.54-1.16a.75.75 0 1 1-.33-1.46c4.57-1.04 8.49-.59 11.65 1.34.36.22.47.68.25 1.03zm1.47-3.27a.94.94 0 0 1-1.29.31c-3.23-1.98-8.15-2.56-11.97-1.4a.94.94 0 1 1-.54-1.8c4.36-1.32 9.78-.68 13.49 1.6.44.27.58.85.31 1.29zm.13-3.4C15.73 8.26 9.1 8.03 5.39 9.16a1.12 1.12 0 1 1-.65-2.15c4.26-1.29 11.58-1.04 16.14 1.66a1.12 1.12 0 1 1-1.15 1.93z" />
    </svg>
  );
}

export function InstagramGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.43.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.43-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.3-1.46.72-2.12 1.38C1.36 2.67.94 3.34.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.3.8.72 1.47 1.38 2.13.66.66 1.33 1.08 2.12 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.7 5.7 0 0 0 2.13-1.38 5.7 5.7 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.7 5.7 0 0 0-1.38-2.12A5.7 5.7 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0z" />
      <path d="M12 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84M12 16a4 4 0 1 1 4-4 4 4 0 0 1-4 4z" />
      <circle cx="18.41" cy="5.59" r="1.44" />
    </svg>
  );
}

export function TikTokGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M16.6 5.82a4.28 4.28 0 0 1-1.08-2.82h-3.2v12.93a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 1 1 .73-5.07V9.9a5.82 5.82 0 0 0-.73-.05A5.79 5.79 0 1 0 15.5 15.6V9.04a7.5 7.5 0 0 0 4.5 1.5V7.36a4.28 4.28 0 0 1-3.4-1.54z" />
    </svg>
  );
}

export function YouTubeGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.5 6.2a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.51A3.02 3.02 0 0 0 .5 6.2 31.5 31.5 0 0 0 0 12a31.5 31.5 0 0 0 .5 5.8 3.02 3.02 0 0 0 2.12 2.14c1.88.51 9.38.51 9.38.51s7.5 0 9.38-.51a3.02 3.02 0 0 0 2.12-2.14A31.5 31.5 0 0 0 24 12a31.5 31.5 0 0 0-.5-5.8zM9.55 15.57V8.43L15.82 12z" />
    </svg>
  );
}

export function FacebookGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.24 2.68.24v2.95h-1.51c-1.49 0-1.96.93-1.96 1.87V12h3.33l-.53 3.47h-2.8v8.38A12 12 0 0 0 24 12z" />
    </svg>
  );
}

/** Amazon / Apple share the same simple note glyph in CD's mockup. */
export function NoteGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 3v9.4A3.3 3.3 0 1 0 14 15.4V8h4V3z" />
    </svg>
  );
}

// ── Stroke icons ─────────────────────────────────────────────────────────────

/** Up-right arrow used in the spotlight delta + mover up-changes. */
export function ArrowUpRight(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17L17 7M9 7h8v8" />
    </svg>
  );
}

/** Down-right arrow used in mover down-changes (CD: M7 7l10 10M9 17h8V9). */
export function ArrowDownRight(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 7l10 10M9 17h8V9" />
    </svg>
  );
}

export function MessageIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export function BellIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

export function SettingsIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

export function ChevronDown(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export function PlusIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** Right-arrow used in "Open →" / "Connect →" / "Full breakdown →" links. */
export function ArrowRight({ size = 13 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
